import * as _ from 'lodash';

import { SBOM_PORTAL, SBOM_PORTAL_PRIVATE } from '../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../typings/permissions';
import { window } from '../../../globals';
import { isFeatureEnabled } from '../../../helpers/features';
import { usePermissions } from '../../../hooks/api';

/**
 * Permissions helpers
 */
const canViewGeneralSettings = (): boolean => window.hasPermission({
  resourceType: ResourceType.OrganizationGeneralSettings,
  action: Action.View,
});

const canViewPortalSettings = (): boolean => isFeatureEnabled(SBOM_PORTAL) && window.hasPermission({
  resourceType: ResourceType.OrganizationPortalSettings,
  action: Action.View,
});

const canViewPortalTokenSettings = (): boolean =>
  isFeatureEnabled(SBOM_PORTAL) &&
  isFeatureEnabled(SBOM_PORTAL_PRIVATE) &&
  canViewPortalSettings() &&
  window.hasPermission({
    resourceType: ResourceType.OrganizationPortalReports,
    action: Action.Manage,
  });

const canEditAuthSettings = (): boolean => window.hasPermission({
  resourceType: ResourceType.OrganizationAuthSettings,
  action: Action.Edit,
});

const canEditIntegrationSettings = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.OrganizationIntegrationSettings,
});

const canViewAnyUser = (): boolean => window.hasPermission({
  resourceType: ResourceType.UserAny,
  action: Action.View,
});

const canEditLanguageSettings = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.OrganizationLanguageSettings,
});

const canEditBillingSettings = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.OrganizationBillingSettings,
});

const canViewNotificationBanners = () => window.on_prem &&
  window.hasPermission({
    resourceType: ResourceType.OrganizationGeneralSettings,
    action: Action.Edit,
  });

const canViewAuditLogs = (): boolean => window.hasPermission({
  action: Action.View,
  resourceType: ResourceType.OrganizationAudit,
});

export const canSetDefaultLicensingPolicy = (): boolean => window.hasPermission({
  action: Action.Set,
  resourceType: ResourceType.OrganizationDefaultLicensingPolicy,
});

export const canSetDefaultSecurityPolicy = (): boolean => window.hasPermission({
  action: Action.Set,
  resourceType: ResourceType.OrganizationDefaultSecurityPolicy,
});

export const canSetDefaultQualityPolicy = (): boolean => window.hasPermission({
  action: Action.Set,
  resourceType: ResourceType.OrganizationDefaultSecurityPolicy,
});

export const canEditDefaultLicensingFilter = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.ProjectIssueFilterLicensingAny,
});

export const canEditDefaultSecurityFilter = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.ProjectIssueFilterSecurityAny,
});

export const canEditDefaultQualityFilter = (): boolean => window.hasPermission({
  action: Action.Edit,
  resourceType: ResourceType.ProjectIssueFilterQualityAny,
});

/**
 * Projects Settings
 */
const canViewProjectIssueSettings = (): boolean => [
  canSetDefaultLicensingPolicy(),
  canSetDefaultSecurityPolicy(),
  canSetDefaultQualityPolicy(),
  canEditDefaultLicensingFilter(),
  canEditDefaultSecurityFilter(),
  canEditDefaultQualityFilter(),
].some(Boolean);

export const shouldDisplayLanguageSettings = canEditLanguageSettings;
export const shouldDisplayBillingSettings = canEditBillingSettings;
export const shouldDisplayAuditLogs = canViewAuditLogs;
export const shouldDisplayOrganizationGeneralSettings = () => canViewGeneralSettings() && canEditAuthSettings();
export const shouldDisplayOrganizationPortalSettings = canViewPortalSettings;
export const shouldDisplayUserSettings = canViewAnyUser;
export const shouldDisplayNotificationBanners = canViewNotificationBanners;
export const shouldDisplayProjectSettings = canViewGeneralSettings;
export const shouldDisplayProjectGeneralSettings = canViewGeneralSettings;
export const shouldDisplayProjectIssueSettings = canViewProjectIssueSettings;
export const shouldDisplayProjectNotificationSettings = canViewGeneralSettings;
export const shouldDisplayProjectUpdateHookSettings = canViewGeneralSettings;
export const shouldDisplayAuthenticationSettings = canEditAuthSettings;
export const shouldDisplayJiraSettings = canEditIntegrationSettings;
export const shouldDisplaySlackSettings = canEditIntegrationSettings;
export const shouldDisplayCustomLicenseScanSettings = canEditIntegrationSettings;
export const shouldDisplayOrganizationPortalTokenSettings = canViewPortalTokenSettings;

export const useCanManageServiceAccounts = (): boolean => {
  const permissionsHook = usePermissions();

  const canManageServiceAccounts = permissionsHook.data?.some((permission) =>
    [ResourceType.ServiceAccount, ResourceType.ServiceAccountTeam].includes(permission.resourceType)
    && permission.action === Action.Manage,
  );

  return canManageServiceAccounts ?? false;
};

export const useCanViewOIDCProviders = (): boolean => {
  const permissionsHook = usePermissions();

  const canViewOIDCProviders = permissionsHook.data?.some((permission) =>
    [ResourceType.OrganizationOIDCProvider, ResourceType.TeamOIDCProvider].includes(permission.resourceType)
    && (permission.action === Action.View || permission.action === Action.Manage),
  );

  return canViewOIDCProviders ?? false;
};

export const useCanCreateOIDCProvider = (): boolean => {
  const permissionsHook = usePermissions();

  const canCreateOIDCProvider = permissionsHook.data?.some((permission) =>
    [ResourceType.OrganizationOIDCProvider, ResourceType.TeamOIDCProvider].includes(permission.resourceType)
    && (permission.action === Action.Manage),
  );

  return canCreateOIDCProvider ?? false;
};

export const useCanManageOIDCProvider = (teamId: number): boolean => {
  const permissionsHook = usePermissions();

  const canManageOIDCProvider = permissionsHook.data?.some((permission) =>
    _.isMatch(permission, {
      resourceType: ResourceType.OrganizationOIDCProvider,
      action: Action.Manage,
    }) || _.isMatch(permission, {
      resourceType: ResourceType.TeamOIDCProvider,
      action: Action.Manage,
      teamId,
    }),
  );

  return canManageOIDCProvider ?? false;
};

export const useCanViewOIDCProviderForTeam = (teamId: number): boolean => {
  const permissionsHook = usePermissions();

  const canView = permissionsHook.data?.some((permission) =>
    (permission.resourceType === ResourceType.OrganizationOIDCProvider &&
     (permission.action === Action.View || permission.action === Action.Manage)) ||
    (permission.resourceType === ResourceType.TeamOIDCProvider &&
     permission.teamId === teamId &&
     (permission.action === Action.View || permission.action === Action.Manage)),
  );

  return canView ?? false;
};

export const useCanViewTrustRelationships = (): boolean => {
  const permissionsHook = usePermissions();

  const canViewTrustRelationships = permissionsHook.data?.some((permission) =>
    [ResourceType.OrganizationServiceAccountTrustRelationship, ResourceType.TeamServiceAccountTrustRelationship]
      .includes(permission.resourceType) && (permission.action === Action.View || permission.action === Action.Manage),
  );

  return canViewTrustRelationships ?? false;
};

export const useCanCreateTrustRelationship = (): boolean => {
  const permissionsHook = usePermissions();

  const canCreateTrustRelationship = permissionsHook.data?.some((permission) =>
    [ResourceType.OrganizationServiceAccountTrustRelationship,
      ResourceType.TeamServiceAccountTrustRelationship].includes(permission.resourceType)
    && (permission.action === Action.Manage),
  );

  return canCreateTrustRelationship ?? false;
};

export const useCanManageTrustRelationship = (trustRelationship?: { scopeId?: number }): boolean => {
  const permissionsHook = usePermissions();

  if (!trustRelationship || !trustRelationship.scopeId) {
    return false;
  }

  const canManageTrustRelationship = permissionsHook.data?.some((permission) =>
    _.isMatch(permission, {
      resourceType: ResourceType.OrganizationServiceAccountTrustRelationship,
      action: Action.Manage,
    }) || _.isMatch(permission, {
      resourceType: ResourceType.TeamServiceAccountTrustRelationship,
      action: Action.Manage,
      teamId: trustRelationship.scopeId,
    }),
  );

  return canManageTrustRelationship ?? false;
};
